import { useTranslate } from "ra-core";
import { useMemo } from "react";

import { getTranslatedCompanySizeLabel } from "../companies/getTranslatedCompanySizeLabel";
import { sizes } from "../companies/sizes";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { toExcelDate, toLabels } from "./cells";
import { customFieldColumns } from "./customFieldColumns";
import type { SheetColumn } from "./types";

/** Spreadsheet columns of companies, in the order of the export and the template. */
export const useCompanyColumns = (): SheetColumn<Company>[] => {
  const translate = useTranslate();
  const {
    acquisitionChannels,
    companySectors,
    customerStatuses,
    eventTypes,
    prospectStates,
    customFields,
  } = useConfigurationContext();

  return useMemo(() => {
    const label = (field: string) =>
      translate(`resources.companies.fields.${field}`);
    const sizeOptions = sizes.map((size) => ({
      value: String(size.id),
      label: getTranslatedCompanySizeLabel(size, translate),
    }));
    const columns: SheetColumn<Company>[] = [
      {
        key: "name",
        header: label("name"),
        group: "identity",
        kind: "text",
        required: true,
        width: 32,
        example: "Banque Lémanique SA",
        get: (company) => company.name,
      },
      {
        key: "customer_status",
        header: label("customer_status"),
        group: "identity",
        kind: "choice",
        options: customerStatuses,
        example: customerStatuses[0]?.label,
        get: (company) => toLabels(company.customer_status, customerStatuses),
      },
      {
        key: "prospect_state",
        header: label("prospect_state"),
        group: "identity",
        kind: "choice",
        options: prospectStates,
        example: prospectStates[0]?.label,
        get: (company) => toLabels(company.prospect_state, prospectStates),
      },
      {
        key: "lead_score",
        header: label("lead_score"),
        group: "identity",
        kind: "rating",
        example: 4,
        get: (company) => company.lead_score,
      },
      {
        key: "sector",
        header: label("sector"),
        group: "identity",
        kind: "choice",
        options: companySectors,
        get: (company) => toLabels(company.sector, companySectors),
      },
      {
        key: "size",
        header: label("size"),
        group: "identity",
        kind: "choice",
        options: sizeOptions,
        example: sizeOptions[2]?.label,
        get: (company) =>
          company.size == null
            ? undefined
            : toLabels(String(company.size), sizeOptions),
      },
      {
        key: "tax_identifier",
        header: label("tax_identifier"),
        group: "identity",
        kind: "text",
        example: "CHE-123.456.789",
        get: (company) => company.tax_identifier,
      },
      {
        key: "description",
        header: label("description"),
        group: "identity",
        kind: "long_text",
        width: 40,
        get: (company) => company.description,
      },
      {
        key: "event_types",
        header: label("event_types"),
        group: "prospecting",
        kind: "choices",
        options: eventTypes,
        example: eventTypes
          .slice(0, 2)
          .map((type) => type.label)
          .join(", "),
        get: (company) => toLabels(company.event_types, eventTypes),
      },
      {
        key: "acquisition_channel",
        header: label("acquisition_channel"),
        group: "prospecting",
        kind: "choice",
        options: acquisitionChannels,
        example: acquisitionChannels[0]?.label,
        get: (company) =>
          toLabels(company.acquisition_channel, acquisitionChannels),
      },
      {
        key: "budget",
        header: label("budget"),
        group: "prospecting",
        kind: "currency",
        example: 12000,
        get: (company) => company.budget,
      },
      {
        key: "distance_km",
        header: label("distance_km"),
        group: "prospecting",
        kind: "number",
        example: 3.5,
        get: (company) => company.distance_km,
      },
      {
        key: "last_contact_date",
        header: label("last_contact_date"),
        group: "prospecting",
        kind: "date",
        get: (company) => toExcelDate(company.last_contact_date),
      },
      {
        key: "email",
        header: label("email"),
        group: "contact",
        kind: "text",
        width: 28,
        example: "contact@banque-lemanique.ch",
        get: (company) => company.email,
      },
      {
        key: "phone_number",
        header: label("phone_number"),
        group: "contact",
        kind: "phone",
        example: "+41 22 123 45 67",
        get: (company) => company.phone_number,
      },
      {
        key: "website",
        header: label("website"),
        group: "contact",
        kind: "text",
        width: 28,
        example: "https://banque-lemanique.ch",
        get: (company) => company.website,
      },
      {
        key: "linkedin_url",
        header: label("linkedin_url"),
        group: "contact",
        kind: "text",
        width: 28,
        get: (company) => company.linkedin_url,
      },
      {
        key: "address",
        header: label("address"),
        group: "address",
        kind: "text",
        width: 28,
        example: "Rue du Rhône 1",
        get: (company) => company.address,
      },
      {
        key: "zipcode",
        header: label("zipcode"),
        group: "address",
        kind: "phone",
        example: "1204",
        get: (company) => company.zipcode,
      },
      {
        key: "city",
        header: label("city"),
        group: "address",
        kind: "text",
        example: "Genève",
        get: (company) => company.city,
      },
      {
        key: "country",
        header: label("country"),
        group: "address",
        kind: "text",
        example: "Suisse",
        get: (company) => company.country,
      },
      {
        key: "sales_id",
        header: label("sales_id"),
        group: "follow_up",
        kind: "text",
        exportOnly: true,
        get: (company, lookups) =>
          company.sales_id != null
            ? lookups.sales.get(company.sales_id)
            : undefined,
      },
      {
        key: "nb_contacts",
        header: label("nb_contacts"),
        group: "follow_up",
        kind: "number",
        exportOnly: true,
        get: (company) => company.nb_contacts,
      },
      {
        key: "created_at",
        header: label("created_at"),
        group: "follow_up",
        kind: "date",
        exportOnly: true,
        get: (company) => toExcelDate(company.created_at),
      },
      ...customFieldColumns<Company>(customFields.companies ?? [], {
        yes: translate("ra.boolean.true"),
        no: translate("ra.boolean.false"),
      }),
    ];
    return columns;
  }, [
    translate,
    acquisitionChannels,
    companySectors,
    customerStatuses,
    eventTypes,
    prospectStates,
    customFields,
  ]);
};
